import * as XLSX from "xlsx";

/**
 * Normalizes whatever shape useTableData(...) returns into a plain row array.
 * Handles the common shapes seen in this codebase without guessing wrong
 * silently — if none match, it returns [] rather than throwing, so a
 * mismatched shape shows up as "0 rows" instead of a crash.
 */
function getRows(tableData) {
  if (Array.isArray(tableData)) return tableData;
  if (Array.isArray(tableData?.data)) return tableData.data;
  if (Array.isArray(tableData?.rows)) return tableData.rows;
  if (Array.isArray(tableData?.items)) return tableData.items;
  if (Array.isArray(tableData?.result)) return tableData.result;
  if (Array.isArray(tableData?.result?.data)) return tableData.result.data;
  return [];
}

/**
 * Builds export rows using ONLY the columns present in `columns`
 * (i.e. filteredConfig.columns — the role-filtered list already computed
 * in the page). This is the security-relevant part: the export must never
 * read raw row objects directly, because a raw row can carry fields this
 * role isn't shown on screen (e.g. another tier's commission/GST figures).
 * Whatever roleMap in WalletSettlementReportsPage hides from the table,
 * the export hides too, because it walks the same filtered column list.
 */
function buildExportRows(tableData, columns) {
  const rows = getRows(tableData);
  return rows.map((row) => {
    const out = {};
    columns.forEach((col) => {
      const label = col.label || col.title || col.header || col.key;
      const value = row?.[col.key];
      out[label] = value === undefined || value === null ? "" : value;
    });
    return out;
  });
}

function safeFilename(base) {
  return String(base).replace(/[^a-zA-Z0-9_-]/g, "_");
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * @param {*} tableData   whatever useTableData(...) returned
 * @param {Array} columns filteredConfig.columns (role-filtered columns only)
 * @param {string} filenameBase
 * @returns {{ok: boolean, count: number}}
 */
export function exportToCSV(tableData, columns, filenameBase = "report") {
  const rows = buildExportRows(tableData, columns);
  if (rows.length === 0) return { ok: false, count: 0 };

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  downloadBlob(csv, `${safeFilename(filenameBase)}.csv`, "text/csv;charset=utf-8;");
  return { ok: true, count: rows.length };
}

export function exportToExcel(tableData, columns, filenameBase = "report") {
  const rows = buildExportRows(tableData, columns);
  if (rows.length === 0) return { ok: false, count: 0 };

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
  XLSX.writeFile(workbook, `${safeFilename(filenameBase)}.xlsx`);
  return { ok: true, count: rows.length };
}